import { useEffect, useMemo, useState } from 'react';

import { pluralize } from '~/client/lib/format';
import { saveDraft } from '~/client/lib/tokens';
import { formatRangeLabel, monthsInRange } from '~/shared/dates';
import type { Poll } from '~/shared/types';
import { nowIsoDay } from '~/shared/tz';

import { Calendar, type AvailMap } from './Calendar';
import { Icon } from './Icons';
import { Button, Chip, Field } from './Primitives';
import { QuickSelect } from './QuickSelect';

interface RespondViewProps {
  poll: Poll;
  initialName?: string;
  initialYes?: Set<string>;
  initialMaybe?: Set<string>;
  isEditing?: boolean;
  submitLabel?: string;
  onSubmit: (name: string, yes: string[], maybe: string[]) => Promise<void> | void;
  onCancel?: () => void;
  submitting?: boolean;
  error?: string;
}

function buildInitialMarks(yes: Set<string>, maybe: Set<string>): AvailMap {
  const m: AvailMap = new Map();
  for (const d of yes) m.set(d, 'yes');
  for (const d of maybe) {
    if (!m.has(d)) m.set(d, 'maybe');
  }
  return m;
}

export function RespondView({
  poll,
  initialName = '',
  initialYes = new Set<string>(),
  initialMaybe = new Set<string>(),
  isEditing = false,
  submitLabel,
  onSubmit,
  onCancel,
  submitting = false,
  error,
}: RespondViewProps) {
  const months = monthsInRange(poll.startDate, poll.endDate);
  const firstMonth = months[0] ?? poll.startDate.slice(0, 7);
  const [year, setYear] = useState(() => Number(firstMonth.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(firstMonth.slice(5, 7)) - 1);
  const [name, setName] = useState(initialName);
  const [marks, setMarks] = useState<AvailMap>(() => buildInitialMarks(initialYes, initialMaybe));
  const todayIso = nowIsoDay(poll.timeZone);

  const { yesDays, maybeDays } = useMemo(() => {
    const yes: string[] = [];
    const maybe: string[] = [];
    for (const [day, status] of marks) {
      if (status === 'yes') yes.push(day);
      else maybe.push(day);
    }
    return { yesDays: yes, maybeDays: maybe };
  }, [marks]);

  useEffect(() => {
    if (isEditing) return;
    saveDraft(poll.id, { name, yes: yesDays, maybe: maybeDays });
  }, [poll.id, name, yesDays, maybeDays, isEditing]);

  const finalSubmitLabel = submitLabel ?? (isEditing ? 'Save changes' : "I'm in");
  const canSubmit = !submitting && name.trim().length > 0;

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    void onSubmit(name.trim(), yesDays, maybeDays);
  };

  const applyQuickSelect = (next: Set<string>) => {
    const m: AvailMap = new Map();
    for (const d of next) m.set(d, 'yes');
    setMarks(m);
  };

  return (
    <main className="app-main">
      <form className="container container-narrow" onSubmit={handleSubmit}>
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          <Chip variant={isEditing ? 'lime' : 'blue'}>
            {isEditing ? "you're in — edit your dates" : "you're a guest"}
          </Chip>
          <span className="mono muted" style={{ fontSize: 12 }}>
            /{poll.id}
          </span>
        </div>
        <h1 className="page-title">{poll.title}</h1>
        <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
          {isEditing
            ? 'tweak whichever days you like. nothing else changes.'
            : 'drop your name, then tap or drag the days you’re free. tap again for "maybe".'}
        </p>

        <div className="col" style={{ gap: 20 }}>
          <Field label="your name" htmlFor="resp-name">
            <input
              id="resp-name"
              className="input"
              placeholder="e.g. jamie"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="given-name"
              maxLength={40}
            />
          </Field>

          <div className="card-surface" style={{ padding: 20 }}>
            <div className="row between" style={{ marginBottom: 12 }}>
              <div className="field-label" style={{ marginBottom: 0 }}>
                your dates · {formatRangeLabel(poll.startDate, poll.endDate)}
              </div>
              <span className="mono muted" style={{ fontSize: 12 }}>
                {yesDays.length} {pluralize(yesDays.length, 'yes')}
                {maybeDays.length > 0 && ` · ${maybeDays.length} maybe`}
              </span>
            </div>
            <QuickSelect start={poll.startDate} end={poll.endDate} onApply={applyQuickSelect} />
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              tap once for <span className="hl-yes">yes</span>, twice for{' '}
              <span className="hl-maybe">maybe</span>, again to clear. drag for bulk yes.
            </div>
            <Calendar
              year={year}
              month={month}
              marks={marks}
              onChangeMarks={setMarks}
              onMonthChange={(y, m) => {
                setYear(y);
                setMonth(m);
              }}
              mode="select"
              triState
              todayIso={todayIso}
              windowStart={poll.startDate}
              windowEnd={poll.endDate}
            />
          </div>

          {error && (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                background: 'var(--coral-soft)',
                color: 'var(--coral-deep)',
                borderRadius: 10,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          <div className="sticky-actions">
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                cancel
              </Button>
            )}
            <Button
              variant="primary"
              size="lg"
              type="submit"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {finalSubmitLabel} <Icon name="check" size={16} />
            </Button>
          </div>
        </div>
      </form>
    </main>
  );
}
